/**
 * Удаление учётной записи.
 *
 * Удаление не мгновенное: запись стирается через сутки, и до того владелец
 * может войти и передумать. Всё это человек должен прочесть до кнопки, а не
 * после, поэтому согласие — отдельной отметкой, а пароль подтверждает, что
 * просит владелец, а не тот, кто сел за открытый браузер.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { errorMessage } from "../lib/authForm";
import { formatDateTime } from "../lib/format";
import { useAuthStore } from "../stores/authStore";
import { Dialog } from "./Dialog";

export function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const requestDeletion = useAuthStore((state) => state.requestDeletion);

  const [agreed, setAgreed] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduled, setScheduled] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      setScheduled((await requestDeletion(password)) ?? "");
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  // Сервер уже закрыл все сессии — здесь выходим и локально. Сначала уход
  // со страницы: профиль закрыт для гостей и иначе успел бы отправить
  // человека на форму входа, а не на главную.
  const leave = () => {
    navigate("/", { replace: true });
    useAuthStore.getState().clear();
  };

  if (scheduled !== null) {
    return (
      <Dialog title={t("deletion.doneTitle")} onClose={leave} dismissible={false}>
        <div className="space-y-4 text-sm text-ink-300">
          <p>{t("deletion.doneText", { date: formatDateTime(scheduled) })}</p>
          <p>{t("deletion.doneCancelHint")}</p>
          <button type="button" className="btn-primary w-full" onClick={leave}>
            {t("deletion.doneButton")}
          </button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={t("deletion.title")} onClose={onClose}>
      <div className="space-y-4 text-sm text-ink-300">
        <ul className="list-disc space-y-2 pl-5">
          <li>{t("deletion.when")}</li>
          <li>{t("deletion.whatGoes")}</li>
          <li>{t("deletion.whatStays")}</li>
          <li>{t("deletion.cancel")}</li>
          <li>{t("deletion.email")}</li>
        </ul>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-ink-700 p-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-amber-400"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
          />
          <span className="text-ink-100">{t("deletion.agree")}</span>
        </label>

        <div>
          <label className="label" htmlFor="delete-password">
            {t("deletion.password")}
          </label>
          <input
            id="delete-password"
            type="password"
            className="field"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        {error && <p className="text-xs text-rust">{error}</p>}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn bg-rust text-ink-50 hover:opacity-90 disabled:opacity-40"
            disabled={!agreed || !password || busy}
            onClick={() => void submit()}
          >
            {busy ? t("deletion.deleting") : t("deletion.confirm")}
          </button>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
